import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './CaughtBanner.module.css'
import AnimatedButton from '../AnimatedButton/AnimatedButton'
import NewModal from '../NewModal/NewModal'
import FirstCallModal from '../FirstCallModal/FirstCallModal'
import NoCallsModal from '../NoCallsModal/NoCallsModal'
import decodeToken from '../../lib/decodeToken'
import useCaughtBannerEligibility from '../../hooks/useCaughtBannerEligibility'

const CaughtBanner = ({
    callCount = 9,
    percentChange = 25,
    onSeeDetails,
    agentName = "Sophia",
    agentAvatar = "/svg/receptionist_profile.png",
    callsAnswered = 42,
    leadsCaptured = 18,
    bookingsMade = 7,
    avgCallValue = 200,
    onTestForwarding,
    onNumberPublished,
    onInvitePeople,
    // Gates the "No calls yet" modal — host passes false until the intro
    // tour has finished, so the tooltip tour shows first, then this modal.
    onboardingReady = true,
    // Restricts the "No calls yet" call-forwarding modal to paid agents.
    // Free/demo users have no live number to forward, so the host passes
    // isPaidPlanAgent here. Defaults true so other stats callers are unaffected.
    noCallsEligible = true,
    // First-call celebration modal (shown when callCount === 1). Data is
    // fetched live from the call-history API using this agentId.
    agentId,
    onViewCallDetails,
}) => {
    const navigate = useNavigate();
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    /*
     * Banner visibility + comparison data. Resolved server-side in the AGENT'S
     * timezone (Monday -> weekly recap, 1st -> monthly recap, both -> slider).
     * This gates ONLY the blue banner below — NoCallsModal / FirstCallModal and
     * the "See full details" modal stay mounted on every other day.
     */
    const {
        show: bannerVisible,
        week: bannerWeek,
        month: bannerMonth,
        avgCallValue: serverAvgCallValue,
        dismiss: dismissBanner,
    } = useCaughtBannerEligibility(agentId);

    // One slide per available period. On a day that is BOTH Monday and the 1st
    // we get two slides and rotate between them.
    const bannerSlides = useMemo(() => {
        const slides = [];
        if (bannerWeek) slides.push({ key: 'week', label: 'vs last week', ...bannerWeek });
        if (bannerMonth) slides.push({ key: 'month', label: 'vs last month', ...bannerMonth });
        return slides;
    }, [bannerWeek, bannerMonth]);

    const [slideIndex, setSlideIndex] = useState(0);

    useEffect(() => {
        if (bannerSlides.length < 2) {
            setSlideIndex(0);
            return undefined;
        }
        const id = setInterval(
            () => setSlideIndex((i) => (i + 1) % bannerSlides.length),
            3000,
        );
        return () => clearInterval(id);
    }, [bannerSlides.length]);

    const slide = bannerSlides[slideIndex] || bannerSlides[0] || null;
    /*
     * Headline: after-business-hours calls of the recapped period, WITH the
     * "You would have missed" line. When that period had NO after-hours calls,
     * the headline falls back to the period's TOTAL calls and the missed line
     * is replaced by a plain "Last week/month calls" label — "missed" only
     * makes sense for after-hours traffic. The trend always compares TOTAL
     * calls period-over-period either way.
     */
    const hasAfterHours = !!slide && slide.afterHoursCalls > 0;
    const bannerCalls = slide
        ? (hasAfterHours ? slide.afterHoursCalls : slide.calls)
        : callCount;
    const bannerSubtext = !slide || hasAfterHours
        ? 'You would have missed'
        : (slide.key === 'month' ? 'Last month calls' : 'Last week calls');
    // percentChange is null when the previous period had zero calls -> "—".
    const hasTrend = slide && typeof slide.percentChange === 'number';

    /*
     * "See full details" mirrors the SAME recap period the banner is showing —
     * the eligibility endpoint already scoped every figure to that window
     * (agent timezone + business hours applied server-side), so there is no
     * second fetch here:
     *   calls               -> Calls Answered (total)
     *   afterHoursCalls     -> Calls You'd Have Missed (outside business hours
     *                          or on a non-working day)
     *   leads               -> calls that captured BOTH a name and an email
     *   bookings            -> appointments created during the period
     *   estimatedValueSaved -> afterHoursCalls x avg call value
     */
    const effectiveAvgCallValue = serverAvgCallValue ?? avgCallValue;
    const answeredVal = slide?.calls ?? callCount;
    const missedVal = slide?.afterHoursCalls ?? 0;
    const leadsVal = slide?.leads ?? 0;
    const bookingsVal = slide?.bookings ?? 0;
    const estimatedValueSaved =
        slide?.estimatedValueSaved ?? missedVal * effectiveAvgCallValue;

    const handleSeeDetailsClick = () => {
        setShowDetailsModal(true);
        if (onSeeDetails) onSeeDetails();
    };
    return (
        <>
            {bannerVisible && (
            <div className={styles.bannerWrapper}>
                <div className={styles.glow} />
                {/* Dismiss — the banner stays until the user closes it, then
                    returns only on the next qualifying day (next Monday / 1st). */}
                <button
                    type="button"
                    className={styles.bannerClose}
                    onClick={dismissBanner}
                    aria-label="Dismiss"
                >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>
                <div className={styles.banner}>


                    <div className={styles.left}>
                        <div className={styles.iconWrap}>
                            <img src="/svg/total_calls_icon.svg" alt="total_calls_icon" />
                        </div>
                        <div className={styles.textBlock}>
                            <p className={styles.eyebrow}>{agentName?.toUpperCase()} CAUGHT</p>
                            <h2 className={styles.callCount}>{bannerCalls} CALLS</h2>
                            <p className={styles.subtext}>{bannerSubtext}</p>
                        </div>
                    </div>

                    <div className={styles.right}>
                        <div className={styles.trend} key={slide?.key || 'trend'}>
                            {hasTrend ? (
                                <>
                                    <span className={styles.arrowUp}>
                                        {slide.percentChange >= 0 ? '↑' : '↓'}
                                    </span>
                                    <span className={styles.percent}>
                                        {Math.abs(slide.percentChange)}%
                                    </span>
                                </>
                            ) : (
                                <span className={styles.percent}>—</span>
                            )}
                            <span className={styles.vsText}>
                                {slide ? slide.label : 'vs last week'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.detailsBtn}
                            onClick={handleSeeDetailsClick}
                        >
                            SEE FULL DETAILS <span className={styles.chevron}>›</span>
                        </button>
                    </div>
                </div>
            </div>
            )}
            <NewModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                title={null}
                height="auto"
                bodyMaxHeight="90vh"
            >
                <div className={styles.summaryModal}>
                    <div className={styles.summaryHead}>
                        <div className={styles.summaryAvatarWrap}>
                            <img src={agentAvatar} alt="Agent" className={styles.summaryAvatar} />
                            <span className={styles.summaryAvatarBadge} />
                        </div>
                        <div className={styles.summaryHeadText}>
                            <p className={styles.summaryMissedLabel}>You would've missed</p>
                            <h2 className={styles.summaryCallCount}>{missedVal} Calls</h2>
                            <p className={styles.summaryHint}>
                                <span className={styles.summaryHintHighlight}>Without Sofia</span>, these callers
                                would never have reached you.
                            </p>
                        </div>
                    </div>

                    <div className={styles.summaryRows}>
                        <div className={`${styles.summaryRow} ${styles.rowPurple}`}>
                            <div className={styles.rowIconWrap}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 4l3 1 1.5 3-2 1.5a11 11 0 0 0 6 6l1.5-2 3 1.5L19 19a2 2 0 0 1-2 2 16 16 0 0 1-15-15 2 2 0 0 1 2-2z" stroke="#6524EB" strokeWidth="1.7" strokeLinejoin="round" />
                                    <path d="M15 4l3 3-3 3" stroke="#6524EB" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className={styles.rowText}>
                                <p className={styles.rowLabel}>Calls Answered</p>
                                <p className={styles.rowSub}>
                                    {slide?.key === 'month'
                                        ? 'Total calls last month'
                                        : 'Total calls last week'}
                                </p>
                            </div>
                            <span className={styles.rowValue}>{answeredVal}</span>
                        </div>

                        <div className={`${styles.summaryRow} ${styles.rowRed}`}>
                            <div className={styles.rowIconWrap}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2c1 4-3 5-3 9a3 3 0 0 0 6 0c1.5 1 2 3 2 4.5A5.5 5.5 0 0 1 6 15.5C6 10 12 8 12 2z" fill="#EF4444" />
                                </svg>
                            </div>
                            <div className={styles.rowText}>
                                <p className={styles.rowLabel}>Calls You'd Have Missed (AI Caught)</p>
                                <p className={styles.rowSub}>After-hours, busy &amp; unanswered elsewhere</p>
                            </div>
                            <span className={styles.rowValue}>{missedVal}</span>
                        </div>

                        <div className={`${styles.summaryRow} ${styles.rowGreen}`}>
                            <div className={styles.rowIconWrap}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="10" cy="8" r="3.5" stroke="#22C55E" strokeWidth="1.7" />
                                    <path d="M4 20a6 6 0 0 1 12 0" stroke="#22C55E" strokeWidth="1.7" strokeLinecap="round" />
                                    <path d="M18 13.3l.86 1.74 1.92.28-1.39 1.35.33 1.91L18 19.66l-1.72.9.33-1.91-1.39-1.35 1.92-.28z" fill="#22C55E" />
                                </svg>
                            </div>
                            <div className={styles.rowText}>
                                <p className={styles.rowLabel}>Leads Captured</p>
                                <p className={styles.rowSub}>Calls with caller intent captured</p>
                            </div>
                            <span className={styles.rowValue}>{leadsVal}</span>
                        </div>

                        <div className={`${styles.summaryRow} ${styles.rowBlue}`}>
                            <div className={styles.rowIconWrap}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <rect x="4" y="5" width="16" height="15" rx="2" stroke="#3B82F6" strokeWidth="1.7" />
                                    <path d="M4 9h16M8 3v4M16 3v4" stroke="#3B82F6" strokeWidth="1.7" strokeLinecap="round" />
                                </svg>
                            </div>
                            <div className={styles.rowText}>
                                <p className={styles.rowLabel}>Bookings Made</p>
                                <p className={styles.rowSub}>Calendar connected</p>
                            </div>
                            <span className={styles.rowValue}>{bookingsVal}</span>
                        </div>

                        <div className={`${styles.summaryRow} ${styles.rowAmber}`}>
                            <div className={styles.rowIconWrap}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 3.5h6l-1.4 2.6H10.4L9 3.5z" fill="#D97706" />
                                    <path d="M10.2 6.1h3.6c2.5 1.2 4.2 3.8 4.2 6.6 0 4-2.9 7.3-6 7.3s-6-3.3-6-7.3c0-2.8 1.7-5.4 4.2-6.6z" stroke="#D97706" strokeWidth="1.6" strokeLinejoin="round" />
                                    <text x="12" y="16.2" fontSize="7" fontWeight="700" fill="#D97706" textAnchor="middle">$</text>
                                </svg>
                            </div>
                            <div className={styles.rowText}>
                                <p className={styles.rowLabel}>Estimated Value Saved</p>
                                <p className={styles.rowSub}>
                                    {missedVal} calls × ${effectiveAvgCallValue} avg. value
                                </p>
                            </div>
                            <span className={styles.rowValueLarge}>
                                ${estimatedValueSaved.toLocaleString()}
                            </span>
                        </div>
                    </div>

                    <AnimatedButton
                        label="Show Calls Details"
                        onClick={() => {
                            setShowDetailsModal(false);
                            // Same destination as the "Total Calls" tile. That
                            // list reads its agent from sessionStorage "agentId"
                            // (NOT "SelectAgentId"), so set the same context the
                            // tile does — otherwise the page shows "No data found".
                            if (agentId) sessionStorage.setItem("agentId", agentId);
                            const uid = decodeToken(localStorage.getItem("token") || "")?.id;
                            if (uid) sessionStorage.setItem("userId", uid);
                            localStorage.setItem("filterType", "single");
                            navigate("/totalcall-list?filter=callHistory");
                        }}
                        positionType="relative"
                        width="100%"
                        bottom="0"
                    />
                </div>
            </NewModal>

            {/* No-calls onboarding lives in its own component. */}
            <NoCallsModal
                callCount={callCount}
                agentId={agentId}
                agentAvatar={agentAvatar}
                onboardingReady={onboardingReady}
                eligible={noCallsEligible}
                onTestForwarding={onTestForwarding}
                onNumberPublished={onNumberPublished}
                onInvitePeople={onInvitePeople}
            />

            {/* First-call celebration lives in its own component. */}
            <FirstCallModal
                callCount={callCount}
                agentId={agentId}
                agentName={agentName}
                onboardingReady={onboardingReady}
                onViewCallDetails={onViewCallDetails}
            />
        </>
    )
}

export default CaughtBanner
